import { Icon } from "@mui/material";
import { WorksheetPricingMode } from "generated/graphql";
import { zodResolver } from "@hookform/resolvers/zod";
import MDBox from "components/MDBox";
import UiButton from "components/base/UiButton";
import UiCard from "components/base/UiCard";
import UiTypography from "components/base/UiTypography";
import { draftColors } from "modules/proposals/draft/draftDesignTokens";
import { useEffect, useMemo, useRef, useState } from "react";
import { toDollars } from "utils/money/toDollars";
import { FormProvider, useFieldArray, useForm, useWatch } from "react-hook-form";
import {
  WorksheetItemInput,
  WorksheetItemInputResolver,
} from "DDD/action-objects/WorksheetItemUpdate";
import WorksheetHeaderFields from "./WorksheetHeaderFields";
import useWorksheetTotals from "./useWorksheetTotals";
import WorksheetSowField from "./WorksheetSowField";
import WorksheetCostSummary from "./WorksheetCostSummary";
import WorksheetCalculationsDrawer from "./WorksheetCalculationsDrawer";
import WorksheetCostSection from "./WorksheetCostSection";
import WorksheetProductTypeField from "modules/worksheetProducts/WorksheetProductTypeField";
import { worksheetHelperErrors } from "./worksheetValidation";
import { resolveHelpers } from "utils/worksheet/worksheetHelpers";
import { WorksheetCostCategory } from "generated/graphql";
import {
  WORKSHEET_CATEGORY_ORDER,
  WorksheetLibrarySelection,
  getBlankWorksheetCostRow,
  librarySelectionToNewRow,
  newRowUuid,
} from "./worksheetFormValues";

type WorksheetItemEditorProps = {
  defaultValues: WorksheetItemInput;
  isReadOnly?: boolean;
  saving?: boolean;
  showProductType?: boolean;
  hideFinancialHeader?: boolean;
  onSave: (values: WorksheetItemInput) => Promise<unknown> | void;
  onSaveAsProduct?: (values: WorksheetItemInput) => Promise<unknown> | void;
  onUpdateLibrary?: (values: WorksheetItemInput) => Promise<unknown> | void;
};

export default function WorksheetItemEditor({
  defaultValues,
  isReadOnly,
  saving,
  showProductType,
  hideFinancialHeader,
  onSave,
  onSaveAsProduct,
  onUpdateLibrary,
}: WorksheetItemEditorProps) {
  const [showCosts, setShowCosts] = useState(defaultValues.costRows.length > 0);
  const [calculationsOpen, setCalculationsOpen] = useState(false);
  const methods = useForm<WorksheetItemInput>({
    resolver: zodResolver(WorksheetItemInputResolver),
    defaultValues,
  });
  const { control, handleSubmit, formState, setValue } = methods;
  const { fields, append, insert, remove, move } = useFieldArray({ control, name: "costRows" });
  const values = useWatch({ control }) as WorksheetItemInput;
  const resolution = useMemo(() => resolveHelpers(values), [values]);
  const totals = useWorksheetTotals(values, resolution);
  const lastValidTotal = useRef(0);

  useEffect(() => {
    if (totals.total > 0) {
      lastValidTotal.current = totals.total;
    }
  }, [totals.total]);

  const rows = fields.map((field, index) => ({
    key: field.id,
    index,
    row: values.costRows?.[index] ?? field,
  }));

  const handleRemoveRow = (index: number) => {
    const isLastRow = (values.costRows?.length ?? 0) <= 1;
    const followsCosts = values.pricingMode === WorksheetPricingMode.COST_BASED;
    const hasTargetOverride = ["profit", "margin"].includes(values.pricingOverrideField ?? "");

    if (isLastRow && (followsCosts || hasTargetOverride)) {
      setValue("fixedTotal", toDollars(lastValidTotal.current || totals.total), {
        shouldDirty: true,
      });
      setValue("pricingMode", WorksheetPricingMode.FIXED, { shouldDirty: true });
      setValue("pricingOverrideField", "none", { shouldDirty: true });
      if (hasTargetOverride) {
        setValue("pricingBaseline", null, { shouldDirty: true });
      }
    }

    remove(index);
  };

  const handleAddSelections = (
    category: WorksheetItemInput["costRows"][number]["category"],
    selection: WorksheetLibrarySelection
  ) => {
    append(librarySelectionToNewRow(category, selection));
  };

  const saveFromCalculations = () => {
    void handleSubmit(async (formValues) => {
      if (validationErrors.length > 0) {
        return;
      }
      const result = await onSave(formValues);

      if (result !== false) {
        setCalculationsOpen(false);
      }
    })();
  };

  const handleAddRow = (category: WorksheetItemInput["costRows"][number]["category"]) => {
    const blankRow = getBlankWorksheetCostRow(category);

    if (category === WorksheetCostCategory.LABOR) {
      const linkedRow = values.costRows?.find((candidate) =>
        ["duration", "travel", "duration_travel"].includes(candidate.quantitySource)
      );

      if (linkedRow) {
        blankRow.quantitySource = linkedRow.quantitySource;
      }
    }

    append(blankRow);
  };

  const handleDuplicateRow = (index: number) => {
    insert(index + 1, {
      ...values.costRows[index],
      id: undefined,
      rowUuid: newRowUuid(),
    });
  };

  const costRowErrorMessages = useMemo(() => {
    const rowErrors = formState.errors.costRows as
      | Record<string, { name?: { message?: string } } | undefined>
      | undefined;

    if (!rowErrors) {
      return [];
    }

    return Object.entries(rowErrors)
      .map(([index, rowError]) => {
        const message = rowError?.name?.message;
        return message ? `Row ${Number(index) + 1}: ${message}` : null;
      })
      .filter((message): message is string => Boolean(message));
  }, [formState.errors.costRows]);

  const validationErrors = useMemo(() => worksheetHelperErrors(values), [values]);
  const displayErrors = [...validationErrors, ...costRowErrorMessages];

  return (
    <UiCard variant="section">
      <FormProvider {...methods}>
        <MDBox
          component="form"
          role="form"
          onSubmit={handleSubmit((formValues) => onSave(formValues))}
          p="8px"
          display="flex"
          flexDirection="column"
          gap="8px"
        >
          {!hideFinancialHeader && (
            <WorksheetHeaderFields isReadOnly={isReadOnly} totals={totals} />
          )}
          {formState.errors.name && (
            <UiTypography uiVariant="body-sm" sx={{ color: draftColors.red }}>
              {formState.errors.name.message}
            </UiTypography>
          )}
          {showProductType && <WorksheetProductTypeField isReadOnly={isReadOnly} />}
          <MDBox display="flex" justifyContent="flex-end">
            <UiButton
              uiVariant="outlined-small"
              disabled={isReadOnly}
              onClick={() => setCalculationsOpen(true)}
            >
              <Icon fontSize="small">calculate</Icon>
              Calculations
            </UiButton>
          </MDBox>
          <WorksheetSowField isReadOnly={isReadOnly} />
          <WorksheetCostSummary
            showCosts={showCosts}
            isReadOnly={isReadOnly}
            totals={totals}
            showExpenseInputs={!hideFinancialHeader}
            onToggleCosts={() => setShowCosts((prev) => !prev)}
          />
          {displayErrors.length > 0 && (
            <MDBox
              sx={{
                border: `1px solid ${draftColors.red}`,
                borderRadius: "8px",
                p: "6px 8px",
                backgroundColor: "#FFF5F5",
              }}
            >
              {displayErrors.map((error, index) => (
                <UiTypography key={index} uiVariant="body-sm" sx={{ color: draftColors.red }}>
                  {error}
                </UiTypography>
              ))}
            </MDBox>
          )}

          {showCosts && (
            <MDBox display="flex" flexDirection="column" gap="8px">
              {WORKSHEET_CATEGORY_ORDER.map((category) => (
                <WorksheetCostSection
                  key={category}
                  category={category}
                  resolution={resolution}
                  rows={rows.filter(({ row }) => row.category === category)}
                  lineQuantity={values.sellingQuantity}
                  lineUnit={values.unit}
                  isReadOnly={isReadOnly}
                  onAdd={() => handleAddRow(category)}
                  onDuplicate={handleDuplicateRow}
                  onDelete={handleRemoveRow}
                  onMove={move}
                  onAddSelections={(selection) => handleAddSelections(category, selection)}
                />
              ))}
            </MDBox>
          )}
          <MDBox display="flex" justifyContent="flex-end" gap="8px" pt="4px">
            {onUpdateLibrary && (
              <UiButton
                uiVariant="outlined"
                disabled={isReadOnly || saving || validationErrors.length > 0}
                onClick={handleSubmit((formValues) => onUpdateLibrary(formValues))}
              >
                Update library product
              </UiButton>
            )}
            {onSaveAsProduct && (
              <UiButton
                uiVariant="outlined"
                disabled={isReadOnly || saving || validationErrors.length > 0}
                onClick={handleSubmit((formValues) => onSaveAsProduct(formValues))}
              >
                Save as product
              </UiButton>
            )}
            <UiButton uiVariant="primary-blue" type="submit" disabled={isReadOnly || saving}>
              {saving ? "Saving..." : "Save"}
            </UiButton>
          </MDBox>
        </MDBox>
        <WorksheetCalculationsDrawer
          open={calculationsOpen}
          onClose={() => setCalculationsOpen(false)}
          onSave={saveFromCalculations}
          saving={saving}
          isReadOnly={isReadOnly}
          resolution={resolution}
        />
      </FormProvider>
    </UiCard>
  );
}
